import { ArrowPathIcon } from "@heroicons/react/24/outline"
import { Button } from "~/ui-rtk/components/ui/controls"

interface ManualEditsBarProps {
  /** Number of rows (shown or hidden by the table filter) whose budget was
   *  hand-edited this session */
  manualEditCount: number
  onReset: () => void
}

/**
 * Toolbar above the recommendations table: reports how many visible rows
 * carry a hand-edited budget (the yellow values) and offers a one-click reset
 * that drops every edit so each row shows its calculated recommendation again.
 *
 * Always rendered — when there is nothing to reset the disabled button tells
 * the user that the value they are looking at is the calculated
 * recommendation (or the live platform budget), not a manual edit.
 */
export default function ManualEditsBar({
  manualEditCount,
  onReset,
}: ManualEditsBarProps) {
  const hasEdits = manualEditCount > 0

  return (
    <div className="flex items-center justify-end gap-3">
      {hasEdits && (
        <span className="text-sm text-gray-400">
          <span className="text-yellow-300 font-medium">
            {manualEditCount} {manualEditCount === 1 ? "row has" : "rows have"}
          </span>{" "}
          a manually edited budget
        </span>
      )}
      <Button
        variant={{ variant: "outlined" }}
        size="sm"
        disabled={!hasEdits}
        title={
          hasEdits
            ? "Discard all manual budget edits and show the calculated recommendations"
            : "No manual budget edits to reset"
        }
        onClick={onReset}
      >
        <ArrowPathIcon className="w-4 h-4" />
        Reset to recommended
      </Button>
    </div>
  )
}
